import { create } from 'zustand';
import { LocationUpdateMessage } from '../services/signalr/connection';

interface HunterLocation {
  userId: string;
  displayName: string;
  latitude: number;
  longitude: number;
  heading?: number;
  accuracy: number;
  role: string;
  lastUpdated: string;
  connectionStatus: 'connected' | 'weak' | 'disconnected';
}

interface GpsSignalState {
  accuracy: number | null;
  isWatching: boolean;
  isLoading: boolean;
  error: string | null;
}

interface LocationState {
  // Other participants' locations
  hunterLocations: Map<string, HunterLocation>;
  
  // My location (for broadcasting)
  myLocation: {
    latitude: number;
    longitude: number;
    accuracy: number;
    heading?: number;
    speed?: number;
  } | null;
  
  // GPS signal state for header indicator
  gpsSignal: GpsSignalState;
  
  // Last broadcast timestamp
  lastBroadcast: number;

  // Actions
  updateHunterLocation: (location: LocationUpdateMessage) => void;
  removeHunter: (userId: string) => void;
  setMyLocation: (location: LocationState['myLocation']) => void;
  setGpsSignal: (signal: Partial<GpsSignalState>) => void;
  setLastBroadcast: (timestamp: number) => void;
  clearLocations: () => void;
}

export const useLocationStore = create<LocationState>()((set) => ({
  hunterLocations: new Map(),
  myLocation: null,
  gpsSignal: {
    accuracy: null,
    isWatching: false,
    isLoading: false,
    error: null,
  },
  lastBroadcast: 0,

  updateHunterLocation: (location: LocationUpdateMessage) =>
    set((state) => {
      const newLocations = new Map(state.hunterLocations);
      newLocations.set(location.userId, {
        userId: location.userId,
        displayName: location.displayName,
        latitude: location.latitude,
        longitude: location.longitude,
        heading: location.heading,
        accuracy: location.accuracy,
        role: location.role,
        lastUpdated: location.lastUpdated,
        connectionStatus: location.connectionStatus,
      });
      return { hunterLocations: newLocations };
    }),

  removeHunter: (userId: string) =>
    set((state) => {
      const newLocations = new Map(state.hunterLocations);
      newLocations.delete(userId);
      return { hunterLocations: newLocations };
    }),

  setMyLocation: (location) => set({ myLocation: location }),

  setGpsSignal: (signal) => set((state) => ({ 
    gpsSignal: { ...state.gpsSignal, ...signal } 
  })),

  setLastBroadcast: (timestamp: number) => set({ lastBroadcast: timestamp }),

  clearLocations: () =>
    set({
      hunterLocations: new Map(),
      myLocation: null,
      gpsSignal: {
        accuracy: null,
        isWatching: false,
        isLoading: false,
        error: null,
      },
      lastBroadcast: 0,
    }),
}));

/**
 * Hook to get hunter locations as an array
 */
export function useHunterLocations(): HunterLocation[] {
  return useLocationStore((state) => Array.from(state.hunterLocations.values()));
}

/**
 * Hook to get a specific hunter's location
 */
export function useHunterLocation(userId: string): HunterLocation | undefined {
  return useLocationStore((state) => state.hunterLocations.get(userId));
}

export type { HunterLocation };
